import { Button, Dropdown, type MenuProps } from 'antd';
import {
  CloudOutlined,
  CodeOutlined,
  FunctionOutlined,
  AppstoreOutlined,
  LinkOutlined,
} from '@ant-design/icons';

interface AwsResourceLinksProps {
  region?: string;
  handlerArn?: string;
  traceId?: string;
  jobId?: string;
  startTime?: string;
  endTime?: string;
}

/**
 * Parse a timestamp string ensuring UTC interpretation.
 * Handles both ISO strings with 'Z' suffix and Timestream format without timezone.
 */
function parseTimestampAsUtc(timestamp: string): number {
  // If it's already a full ISO string with Z or timezone offset, parse directly
  if (timestamp.includes('Z') || timestamp.includes('+') || timestamp.includes('T')) {
    return new Date(timestamp).getTime();
  }
  // Timestream format: "2026-02-23 15:41:21.000000000" - treat as UTC
  // Append 'Z' to ensure UTC interpretation
  return new Date(timestamp.replace(' ', 'T') + 'Z').getTime();
}

function buildCloudWatchLogsUrl(
  region: string,
  logGroupName: string,
  startTime?: string,
  endTime?: string,
  filterPattern?: string
): string {
  const baseUrl = `https://${region}.console.aws.amazon.com/cloudwatch/home`;
  const params = new URLSearchParams();
  params.set('region', region);

  let fragment = `logsV2:log-groups/log-group/${encodeURIComponent(logGroupName).replace(/%/g, '$25')}/log-events`;

  const queryParams: string[] = [];

  // Add filter pattern with quotes for exact match
  if (filterPattern) {
    // Quote the filter pattern for CloudWatch Logs exact phrase matching
    const quotedPattern = `"${filterPattern}"`;
    queryParams.push(`filterPattern$3D${encodeURIComponent(quotedPattern).replace(/%/g, '$25')}`);
  }

  // Add time range with 15-minute buffer on each side to account for any clock drift
  if (startTime) {
    const start = parseTimestampAsUtc(startTime) - 15 * 60 * 1000; // 15 min before
    queryParams.push(`start$3D${start}`);
  }

  if (endTime) {
    const end = parseTimestampAsUtc(endTime) + 15 * 60 * 1000; // 15 min after
    queryParams.push(`end$3D${end}`);
  } else if (startTime) {
    // If no end time, use 30 minutes after start
    const end = parseTimestampAsUtc(startTime) + 30 * 60 * 1000;
    queryParams.push(`end$3D${end}`);
  }

  if (queryParams.length > 0) {
    fragment += `$3F${queryParams.join('$26')}`;
  }

  return `${baseUrl}?${params.toString()}#${fragment}`;
}

function buildLambdaConsoleUrl(region: string, functionArn: string): string {
  // Extract function name from ARN
  const parts = functionArn.split(':');
  const functionName = parts[parts.length - 1];

  return `https://${region}.console.aws.amazon.com/lambda/home?region=${region}#/functions/${functionName}`;
}

function buildEcsConsoleUrl(region: string, clusterName: string, serviceName?: string): string {
  let url = `https://${region}.console.aws.amazon.com/ecs/v2/clusters/${clusterName}`;
  if (serviceName) {
    url += `/services/${serviceName}`;
  }
  return `${url}?region=${region}`;
}

function buildXRayTraceUrl(region: string, traceId: string): string | null {
  // AWS X-Ray trace IDs have format: 1-XXXXXXXX-XXXXXXXXXXXXXXXXXXXXXXXX (35 chars)
  // Skip if the trace ID doesn't match this format
  if (!traceId.match(/^1-[a-f0-9]{8}-[a-f0-9]{24}$/i)) {
    return null;
  }
  return `https://${region}.console.aws.amazon.com/xray/home?region=${region}#/traces/${traceId}`;
}

function getLambdaLogGroupName(functionArn: string): string {
  // Extract function name from ARN and build log group name
  const parts = functionArn.split(':');
  const functionName = parts[parts.length - 1];
  return `/aws/lambda/${functionName}`;
}

function parseArn(arn: string): { service: string; region: string; name: string } | null {
  // ARN format: arn:partition:service:region:account:resource-type/resource-id
  const match = arn.match(/^arn:aws:(\w+):([^:]+):([^:]+):(.+)$/);
  if (!match) return null;

  return {
    service: match[1],
    region: match[2],
    name: match[4],
  };
}

export function AwsResourceLinks({
  region = 'us-east-1',
  handlerArn,
  traceId,
  jobId,
  startTime,
  endTime,
}: AwsResourceLinksProps) {
  const parsedArn = handlerArn ? parseArn(handlerArn) : null;
  const effectiveRegion = parsedArn?.region || region;

  const menuItems: MenuProps['items'] = [];

  // Lambda console link (first, since it's the most useful)
  if (parsedArn?.service === 'lambda') {
    const lambdaUrl = buildLambdaConsoleUrl(effectiveRegion, handlerArn!);
    menuItems.push({
      key: 'lambda',
      icon: <FunctionOutlined />,
      label: (
        <a href={lambdaUrl} target="_blank" rel="noopener noreferrer">
          Lambda Function
        </a>
      ),
    });

    // CloudWatch Logs link for Lambda (uses /aws/lambda/{functionName} log group)
    const logGroupName = getLambdaLogGroupName(handlerArn!);
    const logsUrl = buildCloudWatchLogsUrl(
      effectiveRegion,
      logGroupName,
      startTime,
      endTime,
      jobId
    );

    menuItems.push({
      key: 'cloudwatch-logs',
      icon: <CloudOutlined />,
      label: (
        <a href={logsUrl} target="_blank" rel="noopener noreferrer">
          CloudWatch Logs
        </a>
      ),
    });
  }

  // ECS console link (for ECS tasks)
  if (parsedArn?.service === 'ecs') {
    const ecsUrl = buildEcsConsoleUrl(effectiveRegion, 'pdr-cluster');
    menuItems.push({
      key: 'ecs',
      icon: <AppstoreOutlined />,
      label: (
        <a href={ecsUrl} target="_blank" rel="noopener noreferrer">
          ECS Cluster
        </a>
      ),
    });
  }

  // X-Ray trace link (only if trace ID matches AWS X-Ray format)
  if (traceId) {
    const xrayUrl = buildXRayTraceUrl(effectiveRegion, traceId);
    if (xrayUrl) {
      menuItems.push({
        key: 'xray',
        icon: <CodeOutlined />,
        label: (
          <a href={xrayUrl} target="_blank" rel="noopener noreferrer">
            X-Ray Trace
          </a>
        ),
      });
    }
  }

  if (menuItems.length === 0) {
    return null;
  }

  return (
    <Dropdown menu={{ items: menuItems }} placement="bottomRight">
      <Button icon={<LinkOutlined />}>AWS Resources</Button>
    </Dropdown>
  );
}
